import { useEffect } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { EHANDOVER_TAB_IDS } from '../constants/config';

// 统一承接 EHandover 页面里“初始化与恢复类”的effect。
// 这层只负责什么时候触发这些流程，不改原来的业务函数和执行时机，
// 这样后面再看登录态、首屏预取或导出恢复时，不需要在 index.js 里来回追多组 useEffect。
export default function useEhandoverPageEffects({
  exporting,
  getAccessControlListAPI,
  getIndividualUsers,
  getSettingViewList,
  hasPrefetchedInitialTotalsRef,
  initialTabSetupReady,
  loginInfo,
  loginTabSetupReady,
  pendingAction,
  prefetchInitialOtherTabTotals,
  setExporting,
  setInitialTabSetupReady,
  setLoginTabSetupReady,
  setMyDraftLoaded,
  setPendingAction,
  setTabData,
  replaceTabListState,
  viewSettingReady,
}) {
  // 导出进行中时，持续检查 sessionStorage 里的导出超时标记。
  // 到时后只负责恢复按钮状态，不影响其它页面逻辑。
  useEffect(() => {
    if (exporting) {
      const timeout = sessionStorage.getItem('EHANDOVER_EXPORT') - moment().format('x');

      if (timeout > 0) {
        const timmer = setInterval(() => {
          const newTimeout = sessionStorage.getItem('EHANDOVER_EXPORT') - moment().format('x');
          if (newTimeout <= 0) {
            setExporting(false);
            sessionStorage.removeItem('EHANDOVER_EXPORT');
          }
        }, 1000);

        return () => clearInterval(timmer);
      }
    }
  }, [exporting, setExporting]);

  // 页面首次挂载时，尝试从 sessionStorage 恢复导出中的状态。
  // 这样用户刷新页面后，导出按钮仍能保持原来的禁用/导出中文案。
  useEffect(() => {
    const isExport = sessionStorage.getItem('EHANDOVER_EXPORT');
    if (isExport && moment().isSameOrBefore(moment(isExport, 'x'))) {
      setExporting(true);
    }
  }, [setExporting]);

  // 只有 setting 初始化和登录态相关 tab 初始化都完成后，才允许触发首屏 total 预取。
  // 这里保留原来的 readiness 组合逻辑，避免首屏预取过早执行。
  useEffect(() => {
    if (viewSettingReady && loginTabSetupReady) {
      setInitialTabSetupReady(true);
    }
  }, [loginTabSetupReady, setInitialTabSetupReady, viewSettingReady]);

  // 登录态变化后，重新同步 access、view setting、个人用户信息，以及 my draft / require ack 的显示状态。
  // 这组逻辑是 EHandover 初始化里最容易互相影响的部分，所以单独集中到一个 effect 里。
  useEffect(() => {
    getAccessControlListAPI();
    getSettingViewList();
    setLoginTabSetupReady(false);

    if (loginInfo?.flag) {
      getIndividualUsers();
    }

    setMyDraftLoaded(false);
    replaceTabListState(EHANDOVER_TAB_IDS.MY_DRAFT_TAB, { list: [], next: null, total: 0 });

    const showMyDraftTab = loginInfo?.flag ? true : false;
    setTabData((prev) => {
      const newTabData = _.map(prev, (item) => ({
        ...item,
        isShow: [EHANDOVER_TAB_IDS.MY_DRAFT_TAB, EHANDOVER_TAB_IDS.REQUIRE_ACK_TAB].includes(item?.id)
          ? showMyDraftTab
          : item?.isShow,
      }));
      return newTabData;
    });

    setLoginTabSetupReady(true);
  }, [
    getAccessControlListAPI,
    getIndividualUsers,
    getSettingViewList,
    loginInfo,
    replaceTabListState,
    setLoginTabSetupReady,
    setMyDraftLoaded,
    setTabData,
  ]);

  // 首屏 total 预取只允许执行一次。
  // 这里继续保留 ref 锁，避免登录态、setting 或其它重渲染把首屏预取重复触发。
  useEffect(() => {
    if (!initialTabSetupReady || hasPrefetchedInitialTotalsRef.current) {
      return;
    }
    hasPrefetchedInitialTotalsRef.current = true;
    (async () => {
      await prefetchInitialOtherTabTotals();
    })();
  }, [hasPrefetchedInitialTotalsRef, initialTabSetupReady, prefetchInitialOtherTabTotals]);

  // 登录成功后，继续执行之前因为未登录而挂起的操作。
  // 这里只负责恢复执行，不定义 pendingAction 本身的业务含义。
  useEffect(() => {
    if (pendingAction && loginInfo?.flag && _.isFunction(pendingAction?.callback)) {
      const completeName = loginInfo?.displayName || loginInfo?.corpID;
      pendingAction?.callback(completeName, {corpId: loginInfo?.corpID ?? ""});
      setPendingAction(null);
    }
  }, [loginInfo?.corpID, loginInfo?.displayName, loginInfo?.flag, pendingAction, setPendingAction]);
}
